import React, { useEffect, useMemo, useRef } from 'react';
import Plotly from 'plotly.js-dist';

export type EngagementChartPoint = {
  index: number;
  label: string;
  value: number;
};

type Props = {
  views: EngagementChartPoint[];
  likes: EngagementChartPoint[];
  retweets: EngagementChartPoint[];
};

const PLOT_CONFIG = {
  displayModeBar: false,
  responsive: true,
};

function formatYTick(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  return Math.round(n).toLocaleString();
}

function buildYAxisConfig(y: number[]): {
  y0: number;
  y1: number;
  tickvals: number[];
  ticktext: string[];
} {
  if (y.length === 0) {
    return { y0: 0, y1: 1, tickvals: [0, 0.5, 1], ticktext: ['0', '0', '1'] };
  }
  const yMin = Math.min(...y);
  const yMax = Math.max(...y);
  if (yMin === yMax) {
    const v = yMin;
    const spread = v === 0 ? 1 : Math.max(1, Math.abs(v) * 0.08);
    const y0 = v - spread;
    const y1 = v + spread;
    return {
      y0,
      y1,
      tickvals: [y0, v, y1],
      ticktext: [formatYTick(y0), formatYTick(v), formatYTick(y1)],
    };
  }
  const pad = Math.max((yMax - yMin) * 0.08, 1e-6);
  const y0 = yMin - pad;
  const y1 = yMax + pad;
  const mid = (yMin + yMax) / 2;
  return {
    y0,
    y1,
    tickvals: [yMin, mid, yMax],
    ticktext: [formatYTick(yMin), formatYTick(mid), formatYTick(yMax)],
  };
}

const EngagementPlotlyCard: React.FC<{
  title: string;
  points: EngagementChartPoint[];
  lineColor: string;
}> = ({ title, points, lineColor }) => {
  const plotRef = useRef<HTMLDivElement>(null);

  const plotPayload = useMemo(() => {
    const x = points.map((p) => p.label);
    const y = points.map((p) => p.value);
    const yAxis = buildYAxisConfig(y);

    const trace =
      points.length === 1
        ? {
            x,
            y,
            type: 'scatter' as const,
            mode: 'markers' as const,
            marker: { color: lineColor, size: 9, line: { width: 0 } },
            hovertemplate: '%{x}<br>%{y:,.0f}<extra></extra>',
          }
        : {
            x,
            y,
            type: 'scatter' as const,
            mode: 'lines' as const,
            line: { color: lineColor, width: 2, shape: 'hv' as const },
            hovertemplate: '%{x}<br>%{y:,.0f}<extra></extra>',
          };

    const layout = {
      margin: { l: 4, r: 44, b: 40, t: 4 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(17,24,39,0.5)',
      showlegend: false,
      font: { family: 'system-ui, sans-serif', color: '#9CA3AF', size: 9 },
      xaxis: {
        type: 'category',
        showgrid: false,
        zeroline: false,
        showline: false,
        tickfont: { size: 8, color: '#6B7280' },
        automargin: true,
        tickangle: -28,
        categoryorder: 'array',
        categoryarray: x,
      },
      yaxis: {
        side: 'right' as const,
        showgrid: false,
        zeroline: false,
        showline: false,
        showticklabels: true,
        tickmode: 'array' as const,
        tickvals: yAxis.tickvals,
        ticktext: yAxis.ticktext,
        range: [yAxis.y0, yAxis.y1] as [number, number],
        tickfont: { size: 8, color: '#6B7280' },
        fixedrange: false,
      },
      autosize: true,
    };

    return { data: [trace], layout };
  }, [points, lineColor]);

  useEffect(() => {
    const el = plotRef.current;
    if (!el || points.length === 0) return;

    void Plotly.newPlot(el, plotPayload.data as any, plotPayload.layout as any, PLOT_CONFIG as any);

    return () => {
      Plotly.purge(el);
    };
  }, [plotPayload, points.length]);

  useEffect(() => {
    const el = plotRef.current;
    if (!el || points.length === 0) return;

    const ro = new ResizeObserver(() => {
      Plotly.Plots.resize(el);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [points.length]);

  if (points.length === 0) {
    return (
      <div className="min-w-0 flex-1 rounded border border-gray-700 bg-gray-900/50 p-2">
        <div className="mb-1 text-[11px] font-medium text-gray-300">{title}</div>
        <div className="flex h-32 items-center justify-center text-[10px] text-gray-500">No data</div>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1 rounded border border-gray-700 bg-gray-900/50 p-2">
      <div className="mb-1 text-[11px] font-medium text-gray-300">{title}</div>
      <div ref={plotRef} className="h-32 w-full min-h-[128px] min-w-0" />
    </div>
  );
};

const SocialTweetEngagementCharts: React.FC<Props> = ({ views, likes, retweets }) => {
  return (
    <div className="grid grid-cols-1 gap-2 border-b border-gray-700 bg-gray-900/40 px-2 py-3 sm:grid-cols-3">
      <EngagementPlotlyCard title="Views" points={views} lineColor="#38bdf8" />
      <EngagementPlotlyCard title="Likes" points={likes} lineColor="#f472b6" />
      <EngagementPlotlyCard title="Retweets" points={retweets} lineColor="#a78bfa" />
    </div>
  );
};

export default SocialTweetEngagementCharts;
